/**
 * iter318 wave 15 — SectionRenderer.
 *
 * Dispatches on `section.kind` and renders the CMS section using the
 * marketing site's brand tokens. When the CMS has no sections for a
 * page, the caller falls back to the static hand-tuned layout.
 *
 * Supported kinds: hero | text | grid | cta | faq | stats | html
 */
import { Link } from 'react-router-dom';
import { HtmlSection } from './HtmlSection';
import { BuiltinSection } from './BuiltinSection';

const TINT_CLASS = {
  light: 'bg-white',
  tint: 'bg-[#EAF5FF]',
  dark: 'bg-[#10233F] text-white',
};

function CTAButton({ cta, className }) {
  const base = 'inline-block px-6 py-3 rounded-full text-sm font-semibold transition';
  const variant = {
    primary: 'bg-[#1677FF] hover:bg-[#0e5fd6] text-white',
    secondary: 'bg-white hover:bg-slate-50 text-[#10233F] border border-[#DCE6F0]',
    ghost: 'text-[#1677FF] hover:text-[#0e5fd6]',
  }[cta.variant || 'primary'];
  const cls = `${base} ${variant} ${className || ''}`;
  const isExternal = /^https?:/.test(cta.href || '');
  if (isExternal) {
    return <a href={cta.href} className={cls} data-testid={`cms-cta-${(cta.label || '').slice(0, 24)}`}>{cta.label}</a>;
  }
  return <Link to={cta.href || '/'} className={cls} data-testid={`cms-cta-${(cta.label || '').slice(0, 24)}`}>{cta.label}</Link>;
}

function Ctas({ ctas }) {
  if (!ctas || ctas.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-3 mt-6">
      {ctas.map((c, i) => <CTAButton key={i} cta={c} />)}
    </div>
  );
}

function Eyebrow({ text }) {
  if (!text) return null;
  return <div className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1677FF] mb-3">{text}</div>;
}

function Title({ text, dark }) {
  if (!text) return null;
  return (
    <h2 className={`text-3xl sm:text-4xl font-bold tracking-tight mb-4 ${dark ? 'text-white' : 'text-[#10233F]'}`}>
      {text}
    </h2>
  );
}

function Subtitle({ text, dark }) {
  if (!text) return null;
  return (
    <p className={`text-lg max-w-2xl leading-relaxed ${dark ? 'text-[#B8CADD]' : 'text-[#566579]'}`}>
      {text}
    </p>
  );
}

// ---------------- Kind renderers ----------------

function HeroSection({ s }) {
  return (
    <section className="bg-gradient-to-b from-[#EAF5FF] via-[#F8FBFF] to-white" data-testid={`cms-section-hero-${s.section_key}`}>
      <div className="max-w-6xl mx-auto px-6 py-20 md:py-24">
        <Eyebrow text={s.eyebrow} />
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 max-w-4xl leading-[1.05] text-[#10233F]">
          {s.title}
        </h1>
        <Subtitle text={s.subtitle} />
        <Ctas ctas={s.ctas} />
      </div>
    </section>
  );
}

function TextSection({ s }) {
  const dark = s.tint === 'dark';
  return (
    <section className={TINT_CLASS[s.tint] || TINT_CLASS.light} data-testid={`cms-section-text-${s.section_key}`}>
      <div className="max-w-4xl mx-auto px-6 py-16">
        <Eyebrow text={s.eyebrow} />
        <Title text={s.title} dark={dark} />
        {s.subtitle && <Subtitle text={s.subtitle} dark={dark} />}
        {s.body && (
          <p className={`mt-4 leading-relaxed whitespace-pre-line ${dark ? 'text-[#B8CADD]' : 'text-[#566579]'}`}>
            {s.body}
          </p>
        )}
        <Ctas ctas={s.ctas} />
      </div>
    </section>
  );
}

function GridSection({ s }) {
  const dark = s.tint === 'dark';
  return (
    <section className={TINT_CLASS[s.tint] || TINT_CLASS.light} data-testid={`cms-section-grid-${s.section_key}`}>
      <div className="max-w-6xl mx-auto px-6 py-16">
        <Eyebrow text={s.eyebrow} />
        <Title text={s.title} dark={dark} />
        {s.subtitle && <div className="mb-8"><Subtitle text={s.subtitle} dark={dark} /></div>}
        {s.cards && s.cards.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {s.cards.map((c, i) => (
              <div
                key={i}
                className={
                  dark
                    ? 'bg-white/5 border border-white/10 rounded-2xl p-6'
                    : 'bg-white border border-[#DCE6F0] rounded-2xl p-6'
                }
              >
                <h3 className={`font-semibold text-lg mb-2 ${dark ? 'text-white' : 'text-[#10233F]'}`}>
                  {c.title}
                </h3>
                <p className={`text-sm leading-relaxed ${dark ? 'text-[#B8CADD]' : 'text-[#566579]'}`}>
                  {c.body}
                </p>
              </div>
            ))}
          </div>
        )}
        <Ctas ctas={s.ctas} />
      </div>
    </section>
  );
}

function CtaSection({ s }) {
  return (
    <section className="bg-[#10233F] text-white" data-testid={`cms-section-cta-${s.section_key}`}>
      <div className="max-w-6xl mx-auto px-6 py-16 text-center">
        <Eyebrow text={s.eyebrow} />
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">{s.title}</h2>
        {s.subtitle && (
          <p className="text-[#B8CADD] max-w-2xl mx-auto mb-8">{s.subtitle}</p>
        )}
        {s.ctas && s.ctas.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3">
            {s.ctas.map((c, i) => <CTAButton key={i} cta={c} />)}
          </div>
        )}
      </div>
    </section>
  );
}

function FaqSection({ s }) {
  return (
    <section className={TINT_CLASS[s.tint] || TINT_CLASS.light} data-testid={`cms-section-faq-${s.section_key}`}>
      <div className="max-w-4xl mx-auto px-6 py-16">
        <Title text={s.title} />
        <div className="space-y-4">
          {(s.cards || []).map((c, i) => (
            <details
              key={i}
              className="group bg-[#F8FBFF] border border-[#DCE6F0] rounded-2xl px-6 py-4 open:bg-white transition"
            >
              <summary className="cursor-pointer font-semibold flex items-center justify-between">
                {c.title}
                <span className="text-[#1677FF] group-open:rotate-45 transition">+</span>
              </summary>
              <p className="text-sm text-[#566579] leading-relaxed mt-3 whitespace-pre-line">{c.body}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsSection({ s }) {
  const dark = s.tint === 'dark';
  return (
    <section className={TINT_CLASS[s.tint] || TINT_CLASS.light} data-testid={`cms-section-stats-${s.section_key}`}>
      <div className="max-w-6xl mx-auto px-6 py-16">
        <Title text={s.title} dark={dark} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
          {(s.cards || []).map((c, i) => (
            <div key={i}>
              <div className={`text-4xl font-bold ${dark ? 'text-white' : 'text-[#10233F]'}`}>
                {c.title}
              </div>
              <div className={`text-sm mt-1 ${dark ? 'text-[#B8CADD]' : 'text-[#566579]'}`}>
                {c.body}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const RENDERERS = {
  hero: HeroSection,
  text: TextSection,
  grid: GridSection,
  cta: CtaSection,
  faq: FaqSection,
  stats: StatsSection,
  html: HtmlSection,
  builtin: BuiltinSection,
};

export default function SectionRenderer({ section }) {
  const Comp = RENDERERS[section.kind];
  if (!Comp) return null;
  return <Comp s={section} />;
}
